import { useCallback } from 'react';
import {
  useNavigation,
  useNavigationState,
  useRoute,
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Template } from '@/components/Template/types';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { SetEditorFlow } from '@/contexts/SetEditorContext';
import { useSetEditor } from '@/contexts/SetEditorContext/useSetEditor';

type SetRouteParams = {
  flow?: SetEditorFlow;
  fromTemplate?: string;
};

type SetEditData = {
  name?: string;
  appDisplayName?: string;
} & Record<string, unknown>;

interface UseContinueToSetDetailsParams {
  apiKeyName: string;
  items: unknown[];
  dataKeyName: string;
  dynamicName: string;
  patientId?: string;
  setId?: string;
  editData?: SetEditData | null;
  template?: Template | null;
}

export function useContinueToSetDetails({
  apiKeyName,
  items,
  dataKeyName,
  dynamicName,
  patientId,
  setId,
  editData,
  template,
}: UseContinueToSetDetailsParams) {
  const navigation =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();
  const route = useRoute();
  const { formData, setFormData, setSavePayload } = useSetEditor();

  const routeParams = (route.params ?? {}) as SetRouteParams;
  const flow = routeParams.flow ?? 'createSet';

  const previousRouteName = useNavigationState(
    (state) => state.routes[state.index - 1]?.name,
  );
  const hasPreviousStep =
    previousRouteName === 'SetCreation' ||
    previousRouteName === 'TemplateSelect';

  const continueToDetails = useCallback(() => {
    if (!formData.name) {
      if (editData) {
        setFormData({
          name: editData.name ?? '',
          appDisplayName: editData.appDisplayName ?? '',
        });
      } else if (template) {
        setFormData({
          name:
            flow === 'createTemplate' || flow === 'duplicateTemplate'
              ? `${template.name} (Kopie)`
              : template.name,
          description: template.description,
          difficulty: template.difficulty,
        });
      }
    }
    setSavePayload({
      apiKeyName,
      items,
      dataKeyName,
      dynamicName,
      patientId,
      setId,
      editData,
      fromTemplate: routeParams.fromTemplate,
    });
    navigation.navigate('SetDetails', { flow });
  }, [
    apiKeyName,
    items,
    dataKeyName,
    dynamicName,
    patientId,
    setId,
    editData,
    flow,
    routeParams.fromTemplate,
    template,
    formData.name,
    setFormData,
    setSavePayload,
    navigation,
  ]);

  return { continueToDetails, hasPreviousStep };
}
